import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconCirclePlusFilled,
  IconHistoryOff,
  IconLeaf,
  IconPillFilled,
  IconRestore,
  IconTrash,
} from "@tabler/icons-react";
import MProfilePicker from "@/components/ui/atoms/MProfilePicker";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import MOptionsMenu from "@/components/ui/atoms/MOptionsMenu";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { MedicineItemType, MedicineStatus, PillboxItem } from "@/types/pillbox";
import {
  formatDisplayDate,
  formatFrequencyLine,
  formatMedicineTitle,
  getMemberLabel,
} from "./utils/formatMedicine";
import DeleteMedicationModal from "./components/DeleteMedicationModal";
import styles from "./PastMedications.module.css";

const PastMedications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const { fetchLedger, resumeItem, deleteItem } = usePillbox(memberId);

  // Forwards the List/Schedule tab the landing screen was on before
  // navigating here (see PillboxLanding's quick-action onClick), so going
  // back restores it instead of always landing on the default Schedule tab.
  const handleBackToLanding = () => {
    const fromView = (
      location.state as { fromView?: "schedule" | "list" } | null
    )?.fromView;
    navigate("/pillbox", { state: { viewMode: fromView } });
  };

  const [items, setItems] = useState<PillboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PillboxItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!memberId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchLedger(MedicineStatus.STOPPED)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, fetchLedger]);

  // Opened from this screen — Back/Save on the add-medicine form must
  // return here, not the Pillbox landing screen (see AddMedicine.tsx's
  // returnTo handling).
  const handleAdd = () =>
    navigate("/pillbox/add-medicine", {
      state: { returnTo: location.pathname },
    });
  const handleSelectItem = (item: PillboxItem) =>
    navigate(`/pillbox/${memberId}/${item.itemType}/${item.id}`);

  // Restores instantly with today's start date, the same frequency/times,
  // and no stop date (see PillboxService.resume) — no edit screen, and no
  // navigation away from this list, so the user stays right where they were.
  const handleRestore = async (item: PillboxItem) => {
    if (restoringId) return;
    setRestoringId(item.id);
    try {
      await resumeItem(item.itemType, item.id, { name: item.name });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      showToast({
        variant: "success",
        message: t("pillbox.addMedicine.successMessageRestore", {
          name: formatMedicineTitle(item),
          member: getMemberLabel(memberInfo, "possessive"),
        }),
      });
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    } finally {
      setRestoringId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteItem(deleteTarget.itemType, deleteTarget.id);
      setItems((prev) => prev.filter((i) => i.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <MLoader />
      </div>
    );
  }

  const isEmpty = items.length === 0;

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToLanding}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>
            {t("pillbox.pastMedicationsPage.title")}
          </h1>
          <div className={styles.iconSpacer} />
        </div>
        <div className={styles.switchMemberRow}>
          <MProfilePicker />
          <button
            type="button"
            className={styles.headerAddButton}
            onClick={handleAdd}
            aria-label={t("pillbox.add")}
          >
            <IconCirclePlusFilled size={32} />
          </button>
        </div>
        <p className={styles.subtitle}>
          {t(
            isEmpty
              ? "pillbox.pastMedicationsPage.subtitleEmpty"
              : "pillbox.pastMedicationsPage.subtitle"
          )}
        </p>
      </div>

      <div className={styles.body}>
        {isEmpty ? (
          <MEmptyCard
            className={styles.emptyCard}
            icon={<IconHistoryOff size={64} stroke={1.5} color="#AD95D1" />}
            primaryTitle={t("pillbox.pastMedicationsPage.emptyTitle")}
          />
        ) : (
          <>
            <span className={styles.countLabel}>
              {t("pillbox.pastMedicationsPage.count", { count: items.length })}
            </span>
            <div className={styles.list}>
              {items.map((item) => {
                const isSupplement =
                  item.itemType === MedicineItemType.SUPPLEMENT;
                return (
                  <div key={item.id} className={styles.listItem}>
                    <button
                      type="button"
                      className={styles.listItemButton}
                      onClick={() => handleSelectItem(item)}
                    >
                      <span className={styles.iconBox}>
                        {isSupplement ? (
                          <IconLeaf size={24} stroke={1.5} color="#474747" />
                        ) : (
                          <IconPillFilled size={24} color="#474747" />
                        )}
                      </span>
                      <span className={styles.itemContent}>
                        <span className={styles.itemName}>
                          {formatMedicineTitle(item)}
                        </span>
                        <span className={styles.itemFrequency}>
                          {formatFrequencyLine(
                            item,
                            item.recurrenceIntervalDays
                          )}
                        </span>
                        {item.stoppedAt && (
                          <span className={styles.stoppedBadge}>
                            {t("pillbox.pastMedicationsPage.stoppedOn", {
                              date: formatDisplayDate(item.stoppedAt),
                            })}
                          </span>
                        )}
                      </span>
                    </button>
                    <span className={styles.optionsMenuWrap}>
                      <MOptionsMenu
                        iconSize={24}
                        disabled={restoringId === item.id}
                        options={[
                          {
                            id: "restore",
                            label: t("pillbox.optionsMenu.restore"),
                            icon: <IconRestore size={18} />,
                            onClick: () => handleRestore(item),
                          },
                          {
                            id: "delete",
                            label: t("pillbox.optionsMenu.delete"),
                            icon: <IconTrash size={18} />,
                            onClick: () => setDeleteTarget(item),
                            delete: true,
                          },
                        ]}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <DeleteMedicationModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default PastMedications;
